import { useMemo, useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useProjectDetail } from '@hooks/projects/detail/useProjectDetail';
import { message } from 'antd';
import { getProjectBacklogs, promoteProjectBacklogs } from '@services/projects.service';
import { useCanCheck } from '@/ability/helpers';
import { PROJECT_PMO_SUBMIT_TO_CLIENT_ABILITY as PROJECT_BACKLOGS_PROMOTE_ABILITY } from '@/ability';
import { isTaskOnDevBacklog } from '@hooks/projects/projectHelpers';

export const PROJECT_BACKLOGS_QUERY_KEY = (projectId, params) => ['projects', projectId, 'backlogs', params];

function normalizeBacklogItem(item) {
    if (!item || typeof item !== 'object') return item;
    const requestNotes =
        item.requestNotes ??
        item.request_notes ??
        item.notes ??
        (typeof item.content === 'string' ? { markdown: item.content } : undefined);
    const requestAttachmentsRaw =
        item.requestAttachments ??
        item.request_attachments ??
        item.attachments ??
        item.attachmentUrls ??
        [
            ...(Array.isArray(item.links) ? item.links : []),
            ...(Array.isArray(item.attachments) ? item.attachments : []),
        ];
    return {
        ...item,
        requestNotes,
        requestAttachments: Array.isArray(requestAttachmentsRaw) ? requestAttachmentsRaw : [],
    };
}

/**
 * Flat list of sprint tasks under a backlog item’s milestone (for promote / submit UI).
 * @param {object|null} item backlog row from API
 * @returns {Array<{ sprintTaskId: string, sprintLabel: string, displayLabel: string, isBacklog: boolean }>}
 */
export function getBacklogPromotableSprintTasksFromItem(item) {
    const milestone = item?.milestone;
    const sprints = Array.isArray(milestone?.sprints) ? milestone.sprints : [];
    const rows = [];
    for (const sp of sprints) {
        const sprintLabel = `${sp?.sprintNumber ? `${sp.sprintNumber} · ` : ''}${sp?.name ?? 'Sprint'}`;
        const tasks = Array.isArray(sp?.tasks) ? sp.tasks : [];
        for (const t of tasks) {
            const sprintTaskId = t?.sprintTaskId ?? t?.sprint_task_id;
            if (sprintTaskId == null || sprintTaskId === '') continue;
            const tid = t?.taskNumber ?? t?.taskId ?? t?.id ?? '—';
            rows.push({
                sprintTaskId: String(sprintTaskId),
                sprintLabel: sprintLabel,
                displayLabel: `${tid} · ${t?.title ?? '—'}`,
                isBacklog: isTaskOnDevBacklog(t),
            });
        }
    }
    return rows;
}

/** True when every listed sprint task is on dev backlog (nothing left to promote). */
export function backlogItemAllTasksAreBacklogged(item) {
    const rows = getBacklogPromotableSprintTasksFromItem(item);
    if (rows.length === 0) return false;
    return rows.every((r) => r.isBacklog);
}

function formatMutationError(err) {
    const raw = err?.response?.data?.message ?? err?.response?.data?.error ?? err?.message;
    if (Array.isArray(raw)) return raw.filter(Boolean).join(', ');
    if (raw && typeof raw === 'object') {
        try {
            return JSON.stringify(raw);
        } catch {
            return 'Request failed';
        }
    }
    if (raw != null && raw !== '') return String(raw);
    return 'Request failed';
}

export function useProjectBacklogs(projectId, options = {}) {
    const { enabled = true, initialPage = 1, initialLimit = 10 } = options;
    const [page, setPage] = useState(initialPage);
    const [limit, setLimit] = useState(initialLimit);
    const queryClient = useQueryClient();
    const can = useCanCheck();
    const canPromoteBacklogs = can(PROJECT_BACKLOGS_PROMOTE_ABILITY);

    const { data: project } = useProjectDetail(projectId, { enabled: !!projectId });
    const useTasksEnabled = project?.capabilities?.useTasks !== false;

    const params = useMemo(() => ({ page, limit }), [page, limit]);
    const query = useQuery({
        queryKey: PROJECT_BACKLOGS_QUERY_KEY(projectId, params),
        queryFn: () => getProjectBacklogs(projectId, params),
        enabled: !!projectId && enabled && useTasksEnabled,
        keepPreviousData: true,
    });

    const raw = Array.isArray(query.data?.data) ? query.data.data : [];
    const backlogs = useMemo(() => raw.map(normalizeBacklogItem), [raw]);

    const onPageChange = useCallback(
        (newPage, newPageSize) => {
            setPage(newPage);
            if (newPageSize && newPageSize !== limit) setLimit(newPageSize);
        },
        [limit],
    );

    const promoteMutation = useMutation({
        mutationFn: (sprintTaskIds) => promoteProjectBacklogs(projectId, { sprintTaskIds }),
        onSuccess: () => {
            message.success('Selected tasks were submitted for client review.');
            queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'backlogs'] });
        },
        onError: (err) => {
            message.error(formatMutationError(err));
        },
    });

    return {
        backlogs,
        total: query.data?.total ?? 0,
        page: query.data?.page ?? page,
        limit: query.data?.limit ?? limit,
        totalPages: query.data?.totalPages ?? 1,
        isLoading: query.isPending,
        isError: query.isError,
        refetch: query.refetch,
        setPage,
        setLimit,
        onPageChange,
        canPromoteBacklogs,
        promoteBacklogs: promoteMutation.mutateAsync,
        isPromotingBacklogs: promoteMutation.isPending,
    };
}
